import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, repeatsAClass } from '../skillUsage';

export const ambientParticleFieldSkill: CodeoSkill = {
    name: SKILL_NAMES.ambientParticleField,
    kind: 'codeo',
    description:
        'Scatter a field of small particles — dust, stars, bokeh dots — that drift with parallax ' +
        'and twinkle behind the content, adding depth and motion to otherwise empty negative space.',
    documentUsesSkill: check(
        (script, markup) =>
            (/createElement/.test(script) && /appendChild/.test(script)) ||
            (repeatsAClass(markup) && /yoyo|repeat:/.test(script)),
    ),
    instructions: `# Ambient Particle Field

Empty background space is where a scene feels cheap. A sparse field of small particles — fine dust, distant stars, soft bokeh — fills it with depth: near particles are larger and drift faster, far ones are smaller and slower, and a gentle twinkle keeps the whole field alive.

The particles are generated once at load. **The generation must be deterministic** — if you seed positions with \`Math.random()\`, the live preview and the server render produce *different* layouts, because each loads the page fresh. Use a tiny seeded generator so every render is identical.

## Generate the field (before the timeline)

\`\`\`js
// Deterministic PRNG — same seed → same field in preview and render.
function makeRng(seed) {
    let s = seed >>> 0;
    return () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);
}
const rng = makeRng(20240607);

const field = document.getElementById('particle-field');
const PARTICLES = 48;
for (let i = 0; i < PARTICLES; i++) {
    const depth = rng();                       // 0 = far, 1 = near
    const size = 2 + depth * 6;
    const dot = document.createElement('div');
    dot.className = 'particle';
    dot.dataset.depth = depth.toFixed(3);
    dot.style.cssText =
        'left:' + (rng() * 100).toFixed(2) + '%;' +
        'top:' + (rng() * 100).toFixed(2) + '%;' +
        'width:' + size + 'px;height:' + size + 'px;' +
        'opacity:' + (0.2 + depth * 0.5).toFixed(2) + ';';
    field.appendChild(dot);
}
\`\`\`

\`\`\`html
<div id="particle-field" data-l5-allow-offscreen></div>
\`\`\`

A scattered field is *meant* to run off the edges — dots clipped at the frame are what makes it read as a field extending past the shot rather than a rectangle of dots. \`data-l5-allow-offscreen\` on the container says so, and covers every dot inside it, so the canvas-bounds lint leaves the field alone. Scatter across the full 0–100%; do not inset the dots to keep their boxes inside the canvas.

\`\`\`css
#particle-field { position: absolute; inset: 0; z-index: 0; pointer-events: none; }
.particle {
    position: absolute;
    border-radius: 50%;
    background: #fff;                /* or var(--l5-color-1, #7a6dff) for a brand-tinted bokeh */
    box-shadow: 0 0 6px rgba(255,255,255,0.6);
    /* NO transform here — GSAP owns it */
}
\`\`\`

## Timeline code

Parallax drift (near particles move further) plus a desynchronised twinkle, both finite:

\`\`\`js
const tl = gsap.timeline({ paused: true });
const DURATION = 10;
field.querySelectorAll('.particle').forEach((p, i) => {
    const depth = parseFloat(p.dataset.depth);
    const dy = -(20 + depth * 60);                 // near drifts more
    const period = 5 + (1 - depth) * 4;            // far drifts slower
    const half = period / 2;
    const halfCycles = Math.max(2, Math.round(DURATION / half));
    tl.to(p, { y: dy, duration: half, ease: 'sine.inOut', yoyo: true, repeat: halfCycles - 1 }, 0);
    tl.to(p, { opacity: '*=0.4', duration: 0.8 + (i % 5) * 0.25, ease: 'sine.inOut',
        yoyo: true, repeat: Math.max(1, Math.round(DURATION / (0.8 + (i % 5) * 0.25)) - 1) }, (i % 7) * 0.3);
});
window.__timelines = { main: tl };
\`\`\`

## Adapt

- 30–60 particles for a subtle field; below 20 looks accidental, above ~80 reads as noise (and costs render time).
- White dots = stars/dust; large soft brand-coloured dots with heavier blur = bokeh. Tie the colour to the palette.
- Bias particles toward the edges (skip the centre third) when they'd otherwise sit under a headline.
- For a "rising embers" feel, drift all particles one direction (no yoyo on y) and fade them as they reach the top.

## Pitfalls

- **Determinism is mandatory** — seeded PRNG only, never \`Math.random()\`/\`Date.now()\`/\`performance.now()\`, or preview and the exported video won't match.
- Generated particles animate \`transform\`/\`opacity\` via GSAP, so don't also set \`transform\` on \`.particle\` in CSS.
- **Finite repeats only** — every yoyo is sized to the composition duration; never an infinite repeat.
- Keep the field at \`z-index: 0\` and \`pointer-events: none\` so it never covers or interferes with content.
- Twinkling under text hurts legibility; thin the field or drop opacity wherever it overlaps copy.`,
};
